import React from 'react';
import {
  GridPagination,
  gridPageCountSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium';
import MuiPagination from '@mui/material/Pagination';
import { IconButton, useMediaQuery, Box } from '@mui/material';
import { ComboBox } from '@airbus/components-react';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import styles from './Pagination.module.css';
import PaginationItem from '@mui/material/PaginationItem';

function DesktopPagination(props: {
  page: number;
  onPageChange: (e: any, page: number) => void;
  className?: string;
}) {
  const { page, onPageChange, className } = props;
  const apiRef = useGridApiContext();
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);
  const isSmallScreen = useMediaQuery('(max-width:768px)'); // Adjust breakpoint as needed

  return (
    <MuiPagination
      color='primary'
      className={`paginationDesktop ${className ?? ''}`.trim()}
      count={pageCount}
      page={page + 1}
      showFirstButton
      showLastButton
      siblingCount={isSmallScreen ? 0 : 1}
      boundaryCount={isSmallScreen ? 0 : 1}
      renderItem={(item) => {
        if (
          isSmallScreen &&
          (item.type === 'page' || item.type === 'start-ellipsis' || item.type === 'end-ellipsis')
        ) {
          return null;
        }
        return <PaginationItem {...item} />;
      }}
      onChange={(event, newPage) => onPageChange(event, newPage - 1)}
    />
  );
}

function MobilePagination(props: { page: number; onPageChange: (e: any, page: number) => void }) {
  const { page, onPageChange } = props;
  const apiRef = useGridApiContext();
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);

  const options = React.useMemo(
    () => Array.from({ length: pageCount }, (_, p) => ({ label: `Page ${p + 1}`, value: p })),
    [pageCount],
  );

  const value = options[page] ?? null;

  return (
    <Box className={styles.paginationMobile}>
      <IconButton
        onClick={(e) => onPageChange(e, Math.max(page - 1, 0))}
        disabled={page === 0}
        className={`${styles.paginationNavBtn} ${styles.paginationPrevBtn}`}
      >
        <ArrowBack />
      </IconButton>

      <Box className={styles.paginationCenter}>
        <ComboBox
          className={styles.paginationSelect}
          value={value}
          options={options}
          size='medium'
          keepInputOnSelect
          hideClear
          onChange={(obj) => {
            const next = typeof obj?.value === 'number' ? obj.value : page;
            onPageChange(null, next);
          }}
          onBlur={() => {
            if (!value && typeof page === 'number' && options[page]) {
              onPageChange(null, page);
            }
          }}
        />
      </Box>

      <IconButton
        onClick={(e) => onPageChange(e, Math.min(page + 1, pageCount - 1))}
        disabled={page === pageCount - 1}
        className={`${styles.paginationNavBtn} ${styles.paginationNextBtn}`}
      >
        <ArrowForward />
      </IconButton>
    </Box>
  );
}

export const PaginationComponent = (props: any) => {
  const isMobile = useMediaQuery('(max-width:550px)');

  return (
    <GridPagination
      ActionsComponent={isMobile ? MobilePagination : DesktopPagination}
      {...props}
      className={styles.gridPaginationRoot}
    />
  );
};
